// "İşler" / "Tamamlanan" sekmeleri — Geçmiş Rapor Aktarımı (Task 13).
//
// Tek bileşen, `finished` prop'u ile iki sekme (9A: Tamamlanan'da ilerleme YOK,
// yerine `Bitiş` kolonu). Canlı güncelleme SSE (`event: report_import`) +
// bağlantı koptuğunda 5 sn polling yedeği (`liveOff`). Liste DTO'su
// `unmappedContent` TAŞIMAZ — `UnmappedDrawer` detayı kendi çeker (18A).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  REPORT_IMPORT_STATUSES,
  formatReportingPeriod,
  type ReportImport,
  type ReportImportsListResponse,
} from "@mahir/shared";
import { ApiError } from "../../api/client";
import {
  REPORT_IMPORT_EVENTS_URL,
  archiveImport,
  cancelImport,
  listImports,
  retryImport,
} from "../../api/report-import";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { FilteredTable, useDebouncedValue, type Column } from "../common/FilteredTable";
import type { ClientPickerItem } from "../../lib/client-picker";
import { fillTemplate } from "../../lib/period-ui";
import { usePrefersReducedMotion } from "../../lib/use-media-query";
import {
  IMPORT_COPY,
  STATUS_LABELS,
  formatResultLines,
  totalQuestionsFromStats,
  progressPercent,
  shortBatchId,
  stageLabel,
  statusTone,
  upsertImportRow,
} from "../../lib/report-import-ui";
import { JobsRowActions } from "./JobsRowActions";
import { UnmappedDrawer } from "./UnmappedDrawer";

const PAGE_SIZE = 25;
const POLL_MS = 5000;
/** Backend `finished=true` süzgeci (repository FINISHED_STATUSES) + geri çekilenler. */
const FINISHED_STATUSES: ReportImport["status"][] = ["done", "archived", "failed", "cancelled"];

export interface JobsTabProps {
  finished: boolean;
  canOpenReport: boolean;
  onGoToNew: () => void;
}

type PendingAction = { kind: "cancel" | "archive"; row: ReportImport } | null;

/**
 * Satır aksiyonu hata metni — `cancelRaced` YALNIZ 409 `invalid_state` için;
 * diğer her şey (ağ, 403, 500) genel yenileme mesajına düşer (fix round 1).
 */
function actionErrorText(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) return IMPORT_COPY.cancelRaced;
  return err instanceof Error && err.message ? err.message : IMPORT_COPY.listRefreshFailed;
}

export function JobsTab({ finished, canOpenReport, onGoToNew }: JobsTabProps): JSX.Element {
  const [data, setData] = useState<ReportImportsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 350);
  // Müşteri facet'i ClientPicker — id + ad (çip) tutulur, sorguya yalnız id gider.
  const [client, setClient] = useState<ClientPickerItem | null>(null);
  const clientId = client?.id ?? "";
  const [status, setStatus] = useState("");
  const [batchId, setBatchId] = useState("");
  const [page, setPage] = useState(1);
  // `null` = bağlanıyor — ilk `open`/`error` gelene kadar HİÇBİR etiket gösterilmez
  // (aksi halde her sekme açılışında bir anlığına "5 sn'de bir yenileniyor" yanıp
  // sönüyordu; fix round 1).
  const [live, setLive] = useState<boolean | null>(null);
  const [pending, setPending] = useState<PendingAction>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const reduceMotion = usePrefersReducedMotion();
  // Polling yedeği: "bağlanıyor" (null) durumunda da koşar — SSE hiç açılmazsa
  // liste yine 5 sn'de bir tazelenir.
  const liveRef = useRef<boolean | null>(null);
  liveRef.current = live;
  const dataRef = useRef<ReportImportsListResponse | null>(null);
  dataRef.current = data;
  const loadRef = useRef<() => void>(() => undefined);
  const refreshTimer = useRef<number | null>(null);

  /**
   * SSE satırı listeden çıkarınca / listeye girince sunucu sayaçlarını (`total`,
   * sayfalama) tazeler. Büyük partide 100 olay art arda gelebilir → 800 ms
   * debounce ile TEK istek; iskelet gösterilmez (satırlar yerinde kalır).
   */
  const scheduleRefresh = useCallback((): void => {
    if (refreshTimer.current !== null) window.clearTimeout(refreshTimer.current);
    refreshTimer.current = window.setTimeout(() => {
      refreshTimer.current = null;
      loadRef.current();
    }, 800);
  }, []);
  useEffect(
    () => () => {
      if (refreshTimer.current !== null) window.clearTimeout(refreshTimer.current);
    },
    [],
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, clientId, status, batchId, finished]);

  const load = useCallback((): void => {
    void listImports({
      finished: finished ? "true" : "false",
      ...(clientId ? { clientId } : {}),
      ...(status ? { status: status as ReportImport["status"] } : {}),
      ...(batchId ? { batchId } : {}),
      ...(debouncedSearch ? { q: debouncedSearch } : {}),
      page,
      pageSize: PAGE_SIZE,
    })
      .then((res) => {
        setData(res);
        setListError(null);
      })
      .catch(() => {
        // Satırlar KORUNUR — son bilinen durum gösterilir (8A ERROR).
        setListError(IMPORT_COPY.listRefreshFailed);
      })
      .finally(() => setLoading(false));
  }, [finished, clientId, status, batchId, debouncedSearch, page]);
  loadRef.current = load;

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // ── SSE + polling yedeği ────────────────────────────────────────────────
  useEffect(() => {
    const es = new EventSource(REPORT_IMPORT_EVENTS_URL, { withCredentials: true });
    es.addEventListener("open", () => setLive(true));
    es.addEventListener("error", () => setLive(false));
    const onEvent = (ev: MessageEvent<string>): void => {
      try {
        const parsed = JSON.parse(ev.data) as
          | { type: "import"; row: ReportImport }
          | { type: "queue"; running: number; waiting: number };
        if (parsed.type === "queue") {
          setData((prev) =>
            prev ? { ...prev, queue: { running: parsed.running, waiting: parsed.waiting } } : prev,
          );
          return;
        }
        const belongsHere = FINISHED_STATUSES.includes(parsed.row.status) === finished;
        const known = dataRef.current?.imports.some((r) => r.id === parsed.row.id) === true;
        if (!belongsHere) {
          // Durum değişince satır diğer sekmeye taşınır; `total`/sayfalama sunucudan
          // yeniden okunur (aksi halde tablo boşalır ama "1–2 / 2" yazardı).
          if (known) {
            setData((prev) =>
              prev
                ? { ...prev, imports: prev.imports.filter((r) => r.id !== parsed.row.id) }
                : prev,
            );
            scheduleRefresh();
          }
          return;
        }
        if (!known) {
          // Yeni satır: ilk sayfada listeye eklenir, derin sayfada yalnız sayaç tazelenir.
          if ((dataRef.current?.page ?? 1) > 1) return;
          scheduleRefresh();
        }
        setData((prev) =>
          prev ? { ...prev, imports: upsertImportRow(prev.imports, parsed.row) } : prev,
        );
      } catch {
        // bozuk payload — yok say
      }
    };
    es.addEventListener("report_import", onEvent as EventListener);
    return () => {
      es.removeEventListener("report_import", onEvent as EventListener);
      es.close();
      setLive(null); // sekme değişiminde tekrar "bağlanıyor"
    };
  }, [finished]);

  useEffect(() => {
    const t = window.setInterval(() => {
      if (liveRef.current !== true) load();
    }, POLL_MS);
    return () => window.clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  // ── Aksiyonlar ──────────────────────────────────────────────────────────
  async function runPending(): Promise<void> {
    if (!pending) return;
    const { kind, row } = pending;
    setBusyId(row.id);
    try {
      const res = kind === "cancel" ? await cancelImport(row.id) : await archiveImport(row.id);
      setData((prev) =>
        prev ? { ...prev, imports: upsertImportRow(prev.imports, res.import) } : prev,
      );
      setPending(null);
      scheduleRefresh();
    } catch (err) {
      // `cancelRaced` YALNIZ 409 `invalid_state` içindir (12A) — ağ/500/403 hatasında
      // "Aktarım zaten tamamlandı." demek yalan olurdu (fix round 1).
      setToast(actionErrorText(err));
      setPending(null);
      scheduleRefresh();
    } finally {
      setBusyId(null);
    }
  }

  async function doRetry(row: ReportImport): Promise<void> {
    setBusyId(row.id);
    try {
      const res = await retryImport(row.id);
      setData((prev) =>
        prev ? { ...prev, imports: upsertImportRow(prev.imports, res.import) } : prev,
      );
      scheduleRefresh();
    } catch (err) {
      setToast(actionErrorText(err));
    } finally {
      setBusyId(null);
    }
  }

  // ── Kolonlar ────────────────────────────────────────────────────────────
  const columns = useMemo<Column<ReportImport>[]>(() => {
    const base: Column<ReportImport>[] = [
      {
        key: "client",
        label: IMPORT_COPY.colClient,
        render: (r) => <strong>{r.clientName}</strong>,
      },
      {
        key: "period",
        label: IMPORT_COPY.colPeriod,
        width: "130px",
        render: (r) => formatReportingPeriod(r.reportingYear, r.periodType),
      },
      {
        key: "framework",
        label: IMPORT_COPY.colFramework,
        render: (r) => (
          <span>
            {r.frameworkName} {r.versionNumber}
          </span>
        ),
      },
      {
        key: "batch",
        label: IMPORT_COPY.colBatch,
        width: "80px",
        render: (r) => <span style={monoStyle}>{shortBatchId(r.batchId)}</span>,
      },
      {
        key: "status",
        label: IMPORT_COPY.colStatus,
        width: finished ? "130px" : "210px",
        render: (r) =>
          finished ? (
            <StatusPill row={r} />
          ) : (
            <StatusProgress row={r} reduceMotion={reduceMotion} />
          ),
      },
      {
        key: "result",
        label: IMPORT_COPY.colResult,
        width: "260px",
        render: (r) => <ResultCell row={r} />,
      },
    ];
    if (finished) {
      base.push({
        key: "finishedAt",
        label: IMPORT_COPY.colFinished,
        width: "140px",
        render: (r) => (
          <span style={monoStyle}>
            {r.finishedAt ? new Date(r.finishedAt).toLocaleString("tr-TR") : "—"}
          </span>
        ),
      });
    }
    base.push({
      key: "actions",
      label: IMPORT_COPY.colActions,
      width: "250px",
      render: (r) => (
        <JobsRowActions
          row={r}
          canOpenReport={canOpenReport}
          busyId={busyId}
          onCancel={(row) => setPending({ kind: "cancel", row })}
          onRetry={(row) => void doRetry(row)}
          onArchive={(row) => setPending({ kind: "archive", row })}
          onUnmapped={(row) => setDrawerId(row.id)}
        />
      ),
    });
    return base;
    // `doRetry` kasıtlı olarak dep DEĞİL — her render'da yeniden yaratılan bir
    // fonksiyon; kolonların kimliği yalnız görünümü etkileyen değerlere bağlanır.
  }, [finished, canOpenReport, busyId, reduceMotion]);

  const imports = data?.imports ?? [];
  const queue = data?.queue ?? { running: 0, waiting: 0 };

  return (
    <div className="flex flex-col gap-2">
      {listError ? (
        <div
          role="status"
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            fontSize: "0.82rem",
            background: "var(--color-warning-bg)",
            border: "1px solid var(--color-warning-border)",
            color: "var(--color-warning-text)",
          }}
        >
          {listError}
        </div>
      ) : null}
      {toast ? (
        <div role="alert" style={{ fontSize: "0.82rem", color: "var(--color-text-2)" }}>
          {toast}
        </div>
      ) : null}

      <FilteredTable<ReportImport>
        items={imports}
        columns={columns}
        rowKey={(r) => r.id}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={IMPORT_COPY.searchPlaceholder}
        scrollX
        minTableWidth={1100}
        filters={[
          {
            key: "client",
            // ClientPicker (sunucu aramalı) — seçili ad çipte görünür (9A'nın önek gereği kalktı).
            kind: "client",
            label: `${IMPORT_COPY.colClient} (tümü)`,
            value: client,
            onChange: setClient,
          },
          {
            key: "status",
            label: `${IMPORT_COPY.colStatus} (tümü)`,
            value: status,
            onChange: setStatus,
            options: REPORT_IMPORT_STATUSES.filter(
              (s) => FINISHED_STATUSES.includes(s) === finished,
            ).map((s) => ({ value: s, label: `${IMPORT_COPY.colStatus}: ${STATUS_LABELS[s]}` })),
          },
          {
            key: "batch",
            label: `${IMPORT_COPY.colBatch} ${IMPORT_COPY.filterAllSuffix}`,
            value: batchId,
            onChange: setBatchId,
            options: [...new Set(imports.map((r) => r.batchId))].map((b) => ({
              value: b,
              label: `${IMPORT_COPY.colBatch}: ${shortBatchId(b)}`,
            })),
          },
        ]}
        total={data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        loading={loading}
        emptyMessage={finished ? IMPORT_COPY.emptyDone : IMPORT_COPY.emptyJobs}
        emptyHint={finished ? IMPORT_COPY.emptyDoneHint : IMPORT_COPY.emptyJobsHint}
        emptyAction={
          finished ? undefined : (
            <button
              type="button"
              onClick={onGoToNew}
              style={{
                padding: "5px 12px",
                borderRadius: 6,
                border: "1px solid var(--color-border)",
                background: "var(--color-surface)",
                color: "var(--color-text)",
                fontSize: "0.85rem",
                cursor: "pointer",
              }}
            >
              {IMPORT_COPY.tabNew}
            </button>
          )
        }
        rightActions={
          <span style={{ display: "flex", gap: 10, alignItems: "center", fontSize: "0.78rem" }}>
            <span style={{ color: "var(--color-text-2)" }}>
              {fillTemplate(IMPORT_COPY.queueSummary, {
                running: queue.running,
                waiting: queue.waiting,
              })}
            </span>
            {live !== null ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  color: "var(--color-text-2)",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 999,
                    display: "inline-block",
                    background: live ? "var(--color-primary)" : "var(--color-text-3)",
                  }}
                />
                {live ? IMPORT_COPY.liveOn : IMPORT_COPY.liveOff}
              </span>
            ) : null}
          </span>
        }
      />

      <ConfirmDialog
        open={pending !== null}
        title={pending?.kind === "archive" ? IMPORT_COPY.archiveTitle : IMPORT_COPY.cancelTitle}
        body={
          pending
            ? pending.kind === "archive"
              ? fillTemplate(IMPORT_COPY.archiveConfirm, {
                  client: pending.row.clientName,
                  period: formatReportingPeriod(pending.row.reportingYear, pending.row.periodType),
                  framework: `${pending.row.frameworkName} ${pending.row.versionNumber}`,
                })
              : fillTemplate(IMPORT_COPY.cancelConfirm, {
                  client: pending.row.clientName,
                  period: formatReportingPeriod(pending.row.reportingYear, pending.row.periodType),
                })
            : ""
        }
        confirmLabel={
          pending?.kind === "archive" ? IMPORT_COPY.archiveAction : IMPORT_COPY.cancelAction
        }
        cancelLabel={
          pending?.kind === "archive" ? IMPORT_COPY.cancelGeneric : IMPORT_COPY.cancelKeep
        }
        tone={pending?.kind === "archive" ? "neutral" : "danger"}
        busy={busyId !== null}
        onConfirm={() => void runPending()}
        onCancel={() => setPending(null)}
      />

      <UnmappedDrawer
        importId={drawerId}
        onClose={() => setDrawerId(null)}
        canOpenReport={canOpenReport}
      />
    </div>
  );
}

const monoStyle: React.CSSProperties = {
  fontFamily: "var(--font-mono, monospace)",
  fontSize: "0.78rem",
  color: "var(--color-text-2)",
};

function StatusPill({ row }: { row: ReportImport }): JSX.Element {
  const tone = statusTone(row.status);
  const bg =
    tone === "success"
      ? "color-mix(in srgb, var(--color-success) 12%, white)"
      : tone === "error"
        ? "var(--color-warning-bg)"
        : "var(--color-bg)";
  const fg = tone === "error" ? "var(--color-warning-text)" : "var(--color-text-2)";
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: "0.78rem",
        fontWeight: 600,
        background: bg,
        color: fg,
        // Nötr ton (Sırada / Geri çekildi / İptal edildi) düz metin gibi görünmesin
        // diye kenarlıklı kalır; "Tamam" arka planıyla zaten ayrışır.
        border:
          tone === "error"
            ? "1px solid var(--color-warning-border)"
            : tone === "neutral"
              ? "1px solid var(--color-border)"
              : "1px solid transparent",
      }}
    >
      {STATUS_LABELS[row.status]}
    </span>
  );
}

/** 5A — determinate yalnız `sectionsTotal > 0` iken; aşama metni `aria-live=polite`. */
function StatusProgress({
  row,
  reduceMotion,
}: {
  row: ReportImport;
  reduceMotion: boolean;
}): JSX.Element {
  const active = ["mapping", "extracting", "writing"].includes(row.status);
  const determinate = (row.progress?.sectionsTotal ?? 0) > 0;
  const pct = progressPercent(row.progress);
  if (!active) {
    return (
      <div>
        <StatusPill row={row} />
        <div aria-live="polite" style={{ ...monoStyle, marginTop: 4 }}>
          {stageLabel(row.status, row.progress)}
        </div>
      </div>
    );
  }
  return (
    <div>
      <div
        role="progressbar"
        {...(determinate ? { "aria-valuenow": pct, "aria-valuemin": 0, "aria-valuemax": 100 } : {})}
        aria-label={stageLabel(row.status, row.progress)}
        style={{
          height: 6,
          width: 120,
          borderRadius: 999,
          background: "var(--color-bg)",
          border: "1px solid var(--color-border)",
          overflow: "hidden",
        }}
      >
        <span
          style={{
            display: "block",
            height: "100%",
            background: "var(--color-primary)",
            width: determinate ? `${pct.toString()}%` : "35%",
            transition: reduceMotion ? "none" : "width 220ms ease",
            opacity: determinate ? 1 : 0.6,
          }}
        />
      </div>
      <div aria-live="polite" style={{ ...monoStyle, marginTop: 4 }}>
        {stageLabel(row.status, row.progress)}
      </div>
    </div>
  );
}

/** 5A — sonuç hücresi: birincil satır + amber istisnalar + mono detay. */
function ResultCell({ row }: { row: ReportImport }): JSX.Element {
  if (row.status === "failed") {
    return (
      <div style={{ fontSize: "0.82rem", color: "var(--color-text-2)" }}>
        {row.failureReason === "llm_auth" ? (
          <span>
            {IMPORT_COPY.authFailedRow.split(" — ")[0]} —{" "}
            <Link
              to="/admin/settings/llm"
              style={{ color: "var(--color-primary)", textDecoration: "underline" }}
            >
              {IMPORT_COPY.settingsLink}
            </Link>
          </span>
        ) : (
          (row.failureMessage ?? IMPORT_COPY.resultEmpty)
        )}
      </div>
    );
  }
  if (!row.stats) return <span style={monoStyle}>—</span>;
  const total = totalQuestionsFromStats(row.stats);
  const lines = formatResultLines(row.stats, total);
  return (
    <div>
      <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>{lines.primary}</div>
      {lines.exceptions ? (
        <div style={{ fontSize: "0.78rem", color: "var(--color-warning-text)" }}>
          {lines.exceptions}
        </div>
      ) : null}
      <div style={monoStyle}>{lines.details}</div>
    </div>
  );
}
